/**
 * NotificationMenu
 *
 * Top-bar bell-icon dropdown. Shows the latest few notifications and
 * a "See all" action that routes to the dedicated page. Auto-marks
 * the displayed unread ones as read on open so the badge count
 * settles immediately — the read state is also reflected in the row's
 * appearance.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Menu, List, Box, Typography, Button } from '../ui';
import { NotificationsIcon, ChevronRightIcon } from '../icons';
import NotificationItem from './NotificationItem';
import { RootState } from '../../types';
import {
  setNotifications,
  markAsRead,
} from '../../features/notifications/notificationsSlice';
import { notificationsClient } from '../../apis/client-instances';
import { alert } from '../../hooks/function';
import { useSnackbar } from 'notistack';

interface NotificationMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const NotificationMenu: React.FC<NotificationMenuProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const notifications = useSelector(
    (state: RootState) => state.notifications.notifications,
  );

  useEffect(() => {
    if (open) fetchRecentNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchRecentNotifications = async () => {
    try {
      const response = await notificationsClient.getNotifications(3, 0, false);
      const data = await response.data.text();
      const parsed = JSON.parse(data);
      dispatch(setNotifications(parsed.notifications || []));

      // Auto-mark displayed notifications as read so the bell badge
      // reflects "you've seen these" without a per-row action.
      const unreadIds =
        parsed.notifications
          ?.filter((n: any) => !n.isRead)
          .map((n: any) => n.id) || [];

      if (unreadIds.length > 0) {
        await notificationsClient.markAsRead({ notificationIds: unreadIds });
        dispatch(markAsRead(unreadIds));
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      alert(enqueueSnackbar, 'Failed to load notifications', 'error');
    }
  };

  const handleSeeAll = () => {
    onClose();
    navigate('/notifications');
  };

  const displayedNotifications = notifications.slice(0, 3);
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            width: 420,
            maxHeight: 540,
            mt: 1,
            borderRadius: 2,
            // Softer drop shadow than the MUI default — reads as a
            // floating card rather than a popover.
            boxShadow: '0 18px 40px -16px rgba(15,40,80,0.18), 0 4px 12px rgba(15,40,80,0.06)',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          },
        },
      }}
      MenuListProps={{
        sx: { p: 0 },
        disablePadding: true,
      }}
    >
      {/* Header band — title, subtitle count, no harsh divider line. */}
      <Box
        sx={{
          px: 2,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'rgba(var(--brand-rgb), 0.06)',
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'rgba(var(--brand-rgb), 0.18)',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <NotificationsIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            Notifications
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {unreadCount > 0
              ? `${unreadCount} unread`
              : notifications.length > 0
                ? 'All caught up'
                : 'No notifications yet'}
          </Typography>
        </Box>
      </Box>

      {displayedNotifications.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 5,
            px: 3,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'grey.100',
              color: 'text.disabled',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <NotificationsIcon sx={{ fontSize: 28 }} />
          </Box>
          <Typography variant="body2" color="text.secondary" align="center">
            You're all caught up.
          </Typography>
          <Typography variant="caption" color="text.disabled" align="center">
            New assignments and updates will show up here.
          </Typography>
        </Box>
      ) : (
        <List sx={{ py: 0 }}>
          {displayedNotifications.map((notification) => (
            <NotificationItem
              condensed
              key={notification.id}
              notification={notification}
              onClick={onClose}
            />
          ))}
        </List>
      )}

      {notifications.length > 0 && (
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Button
            onClick={handleSeeAll}
            fullWidth
            variant="text"
            endIcon={<ChevronRightIcon />}
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 1.5,
              '&:hover': { bgcolor: 'rgba(var(--brand-rgb), 0.10)' },
            }}
          >
            See all notifications
          </Button>
        </Box>
      )}
    </Menu>
  );
};

export default NotificationMenu;
